import type { SourceItem } from '@paladigm/answer-visuals';

import type { DataNote, StepHealth } from './types';

/**
 * Data health and sources under an answer (iq-ai-cio `docs/features/streaming-events.md`,
 * "Data health and sources"): what each step's TASK_COMPLETED said came back, read with the
 * same rule as iq-ai-cio's own web client. A reply whose frames carried none of the fields
 * shows nothing.
 */

/** A step's `data_state` that is a gap, with the sentence shown when no note says more. */
export const GAP_STATES: Record<string, string> = {
  empty: 'No data came back.',
  partial: 'Only part of the data came back.',
  unresolved: 'The name or symbol could not be matched.',
  unavailable: 'The source was unavailable.',
};

/**
 * Notes that say what the figures cover, not what is missing: shown as a plain line, never as
 * a gap. The CRM's scope note is one on every CRM read; `fetched_not_dated` is on every plain
 * metrics lookup.
 */
export const INFO_CODES: ReadonlySet<string> = new Set([
  'firm_wide_scope',
  'filtered_scope',
  'us_only_macro',
  'fetched_not_dated',
]);

function noteTexts(notes: DataNote[], info: boolean): string[] {
  const texts = notes
    .filter((note) => INFO_CODES.has(note.code) === info)
    .map((note) => note.text.trim())
    .filter(Boolean);
  return [...new Set(texts)];
}

export interface Gap {
  /** The step, as the status line named it, when it was named. */
  step?: string;
  text: string;
}

/**
 * Every step whose data has a gap, in the reply's order: its gap notes' texts (any code but the
 * informational ones), or else, for a `data_state` that is a gap, that state's sentence. A step
 * that is `ok` with only informational notes, or with neither field, has none. The same step
 * and text are shown once.
 */
export function replyGaps(
  health: StepHealth[] | undefined,
  stateText: (state: string) => string,
): Gap[] {
  const gaps: Gap[] = [];
  const seen = new Set<string>();
  for (const step of health ?? []) {
    const texts = noteTexts(step.notes, false);
    const text =
      texts.length > 0
        ? texts.join(' ')
        : step.dataState && step.dataState in GAP_STATES
          ? stateText(step.dataState)
          : '';
    if (!text) continue;
    const key = `${step.step ?? ''}\u0000${text}`;
    if (seen.has(key)) continue;
    seen.add(key);
    gaps.push({ ...(step.step ? { step: step.step } : {}), text });
  }
  return gaps;
}

/** Every informational note text in the reply, each once, in the reply's order. */
export function replyInfo(health: StepHealth[] | undefined): string[] {
  const texts = new Set<string>();
  for (const step of health ?? []) {
    for (const text of noteTexts(step.notes, true)) texts.add(text);
  }
  return [...texts];
}

function sourceKey(item: SourceItem): string {
  return JSON.stringify([item.title, item.publisher ?? '', item.date ?? '', item.url ?? '']);
}

/** The reply's sources so far with a step's added, each source once, in arrival order. */
export function withSources(prev: SourceItem[] | undefined, added: SourceItem[]): SourceItem[] {
  const next = [...(prev ?? [])];
  const seen = new Set(next.map(sourceKey));
  for (const item of added) {
    const key = sourceKey(item);
    if (seen.has(key)) continue;
    seen.add(key);
    next.push(item);
  }
  return next;
}
